import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useCopy } from './copy/copyMode';
function FeatureItem({
  text
}: {
  text: string;
}) {
  return <li className="flex items-start gap-3 text-sm md:text-base text-[#2B211A]" data-id="element-1896">
      <div className="w-1.5 h-1.5 bg-[#2B211A] mt-2 shrink-0" data-id="element-1897" />
      <span className="leading-relaxed" data-id="element-1898">{text}</span>
    </li>;
}
export function PricingSection() {
  const copy = useCopy();
  return <section className="relative w-full bg-[#FAF8F4] py-16 md:py-32 overflow-hidden" data-id="element-1899">
      {/* Halftone dot-matrix texture — matches the other cream sections */}
      <div aria-hidden="true" className="absolute inset-0 dot-matrix opacity-40 pointer-events-none z-0" data-id="element-1900" />
      <div className="max-w-[1280px] mx-auto px-6 md:px-10 relative z-10" data-id="element-1901">
        {/* Header */}
        <div className="mb-12 md:mb-16" data-id="element-1902">
          <h2 className="text-3xl md:text-5xl font-title font-medium text-[#2B211A] tracking-tight leading-[1.1]" data-id="element-1903">
            {copy.pricingSection.headline}
          </h2>
        </div>

        {/* Grid Container */}
        <div className="relative border-t border-b border-dotted border-neutral-300" data-id="element-1904">
          {/* Vertical dotted lines for desktop grid */}
          <div className="hidden md:block absolute top-0 bottom-0 left-1/3 w-px border-l border-dotted border-neutral-300" data-id="element-1905" />
          <div className="hidden md:block absolute top-0 bottom-0 left-2/3 w-px border-l border-dotted border-neutral-300" data-id="element-1906" />
          <div className="hidden md:block absolute top-0 bottom-0 left-0 w-px border-l border-dotted border-neutral-300" data-id="element-1907" />
          <div className="hidden md:block absolute top-0 bottom-0 right-0 w-px border-l border-dotted border-neutral-300" data-id="element-1908" />

          <div className="grid grid-cols-1 md:grid-cols-3 relative z-10" data-id="element-1909">
            {/* Starter Tier — paid, opens with a 7-day free trial */}
            <div className="p-6 md:p-8 border-b border-dotted border-neutral-300 md:border-b-0 flex flex-col" data-id="element-1910">
              <div className="bg-[#F3EFEA] rounded-2xl p-6 md:p-8 mb-8 h-48 flex flex-col justify-between" data-id="element-1911">
                <h3 className="font-title font-normal text-2xl text-[#2B211A]" data-id="element-1912">
                  Starter
                </h3>
                <div data-id="element-1913">
                  <div className="text-neutral-500 text-lg" data-id="element-1914">
                    <span className="text-2xl text-[#2B211A]" data-id="element-1915">$49</span>
                    /month
                  </div>
                  <span className="inline-flex mt-2 items-center gap-1.5 text-xs font-semibold text-[#EA580C] bg-[#FBEAE0] px-2.5 py-1 rounded-full" data-id="element-1916">
                    7-day free trial
                  </span>
                </div>
              </div>
              <ul className="space-y-3 mb-10 flex-1" data-id="element-1917">
                <FeatureItem text="1 workspace, unlimited members" data-id="element-1918" />
                <FeatureItem text="Starter monthly credit allowance" data-id="element-1919" />
                <FeatureItem text="Slack-native agent in threads + mentions" data-id="element-1920" />
                <FeatureItem text="Integrations + tool execution" data-id="element-1921" />
                <FeatureItem text="Scheduled tasks & crons" data-id="element-1922" />
                <FeatureItem text="Drafts + artifacts where supported" data-id="element-1923" />
              </ul>
              <Link to="/signup" className="w-full flex justify-center bg-[#2B211A] hover:bg-[#1C1510] text-white py-3 rounded-xl font-medium transition-colors" data-id="element-1924">
                Start 7-day trial
              </Link>
            </div>

            {/* Pro Tier — paid, opens with a 7-day free trial */}
            <div className="p-6 md:p-8 border-b border-dotted border-neutral-300 md:border-b-0 flex flex-col" data-id="element-1925">
              <div className="bg-[#F3EFEA] rounded-2xl p-6 md:p-8 mb-8 h-48 flex flex-col justify-between" data-id="element-1926">
                <h3 className="font-title font-normal text-2xl text-[#2B211A]" data-id="element-1927">
                  Pro
                </h3>
                <div data-id="element-1928">
                  <div className="text-neutral-500 text-lg" data-id="element-1929">
                    <span className="text-2xl text-[#2B211A]" data-id="element-1930">$199</span>
                    /month
                  </div>
                  <span className="inline-flex mt-2 items-center gap-1.5 text-xs font-semibold text-[#EA580C] bg-[#FBEAE0] px-2.5 py-1 rounded-full" data-id="element-1931">
                    7-day free trial
                  </span>
                </div>
              </div>
              <ul className="space-y-3 mb-10 flex-1" data-id="element-1932">
                <FeatureItem text="Multiple workspaces, unlimited members" data-id="element-1933" />
                <FeatureItem text="Higher monthly credit allowance" data-id="element-1934" />
                <FeatureItem text="Slack-native agent in threads + mentions" data-id="element-1935" />
                <FeatureItem text="Integrations + tool execution" data-id="element-1936" />
                <FeatureItem text="Scheduled tasks & crons" data-id="element-1937" />
                <FeatureItem text="Drafts + artifacts where supported" data-id="element-1938" />
              </ul>
              <Link to="/signup" className="w-full flex justify-center bg-[#EA580C] hover:bg-[#C2410C] text-white py-3 rounded-xl font-medium transition-colors" data-id="element-1939">
                Start 7-day trial
              </Link>
            </div>

            {/* Custom Tier — enterprise, contact sales */}
            <div className="p-6 md:p-8 flex flex-col" data-id="element-1940">
              <div className="bg-[#F3EFEA] rounded-2xl p-6 md:p-8 mb-8 h-48 flex flex-col justify-between" data-id="element-1941">
                <div data-id="element-1942">
                  <span className="block text-[11px] font-bold tracking-[2px] uppercase text-[#EA580C] mb-2" data-id="element-1943">
                    Enterprise
                  </span>
                  <h3 className="font-title font-normal text-2xl text-[#2B211A]" data-id="element-1944">
                    Custom
                  </h3>
                </div>
                <div className="text-neutral-500 text-lg" data-id="element-1945">Flexible pricing</div>
              </div>
              <ul className="space-y-3 mb-10 flex-1" data-id="element-1946">
                <FeatureItem text="Everything in Pro" data-id="element-1947" />
                <FeatureItem text="Invoicing + custom billing terms" data-id="element-1948" />
                <FeatureItem text="Security review support + DPA" data-id="element-1949" />
                <FeatureItem text="SLA + priority support" data-id="element-1950" />
                <FeatureItem text="Dedicated onboarding + tailored limits/controls" data-id="element-1951" />
              </ul>
              <Link to="/contact" className="w-full flex justify-center bg-[#2B211A] hover:bg-[#1C1510] text-white py-3 rounded-xl font-medium transition-colors" data-id="element-1952">
                Contact sales
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>;
}